// RichTextEditor.js
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import * as React from 'react';

import {
  MenuButtonAlignCenter,
  MenuButtonAlignLeft,
  MenuButtonAlignRight,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonHighlightColor,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  RichTextEditorProvider,
  RichTextField,
} from 'mui-tiptap';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

/** Simple link editor button that DOES NOT depend on any bubble/menu internals */
function SimpleLinkButton({ editor }) {
  const [open, setOpen] = React.useState(false);
  const [href, setHref] = React.useState('');
  const [text, setText] = React.useState('');

  if (!editor) return null;

  const openDialog = () => {
    const prevHref = editor.getAttributes('link')?.href ?? '';
    setHref(prevHref);

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    setText(selectedText);
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const apply = () => {
    const raw = href.trim();

    if (!raw) {
      editor.chain().focus().unsetLink().run();
      setOpen(false);
      return;
    }

    const normalized = /^(https?:)?\/\//i.test(raw) ? raw : `https://${raw}`;
    const chain = editor.chain().focus();
    const { empty, from } = editor.state.selection;

    if (empty) {
      const display = (text || normalized).trim();
      if (display.length) {
        chain.insertContent(display).setTextSelection({ from, to: from + display.length });
      }
    }

    chain.extendMarkRange('link').setLink({ href: normalized }).run();
    setOpen(false);
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={openDialog}>
        Link
      </Button>

      <Dialog open={open} onClose={closeDialog}>
        <DialogTitle>Edit link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="https://example.com"
          />
          <TextField
            margin="dense"
            label="Text (optional, used if nothing is selected)"
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={apply} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const RichTextEditor = React.memo(function RichTextEditor(props) {
  const { value = '', onEditorChange, size = 'standard' } = props;
  const containerRef = React.useRef(null);
  const isFocused = React.useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
    ],
    content: value,
    onFocus: () => {
      isFocused.current = true;
    },
    onBlur: ({ editor, event }) => {
      isFocused.current = false;
      // Ignore blurs into our own controls/dialog
      if (event?.relatedTarget && containerRef.current?.contains(event.relatedTarget)) return;
      onEditorChange?.(editor.getHTML());
    },
    onUpdate: ({ editor }) => {
      if (editor.getHTML() === value) {
        return;
      }
      onEditorChange?.(editor.getHTML());
    },
  });

  React.useEffect(() => {
    if (editor && typeof value === 'string') {
      const isSame = editor.getHTML() === value;
      if (!isSame && !isFocused.current) {
        editor.commands.setContent(value, false);
      }
    }
  }, [editor, value]);

  const height =
    size === 'small' ? 100 :
    size === 'medium' ? 200 :
    size === 'auto' ? undefined : 500;

  return (
    <div ref={containerRef}>
      <RichTextEditorProvider editor={editor}>
        <RichTextField
          sx={{
            height,
            '.ProseMirror': {
              minHeight: height ?? 200,
              overflowY: 'auto',
              outline: 'none',
            },
          }}
          controls={
            <MenuControlsContainer>
              <MenuButtonUndo />
              <MenuButtonRedo />
              <MenuDivider />
              <MenuButtonAlignLeft />
              <MenuButtonAlignCenter />
              <MenuButtonAlignRight />
              <MenuDivider />
              <MenuButtonIndent />
              <MenuButtonUnindent />
              <MenuDivider />
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonHighlightColor />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuDivider />
              <MenuButtonRemoveFormatting />
              {/* Our custom button — pass the editor explicitly */}
              <SimpleLinkButton editor={editor} />
            </MenuControlsContainer>
          }
        />
      </RichTextEditorProvider>
    </div>
  );
});

export default RichTextEditor;
